<template>
  <main works-article :key="$route.params.slug" :style="`background-color:#${bgColor}`">
    <p-section class="hero" :theme="`${theme}`">
      <div class="inner-wrap">
        <div class="text-area">
          <h2 v-html="enterToBr(title)" :class="{ lgTitle }"></h2>
          <ul class="tags">
            <li v-for="tag in heroTags" :key="tag"><a @click="onSearch(tag)">{{ tag }}</a></li>
          </ul>
        </div>
      </div>
    </p-section>
    <section class="content" :class="type">
      <template v-for="(item, i) in content">
        <Component :is="item.comp" :value="item" :key="`${item.type}-${i}`" v-if="item.value"/>
        <hr class="line" :key="`${item.type}-${i}`" v-else-if="item.comp === 'AppendixLine'"/>
      </template>
    </section>
    <section class="tag" v-if="!isEmpty(tags)">
      <ul class="tags">
        <li v-for="tag in tags" :key="tag"><a @click="onSearch(tag)">{{ tag }}</a></li>
      </ul>
    </section>
    <section class="related" v-if="!isEmpty(related)">
      <h3>Related works</h3>
      <Slider scrollbar :navigation="related && related.length > 2" slidesPerView="auto" :breakpoints="{
        768: {
          slidesPerView: 2,
          spaceBetween: 20
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 20
        }
      }" :key="`highlights-${lang}-${info.sq}`">
        <div v-for="item in related" :key="item.sq">
          <div class="slider-item">
            <div class="inner" @click="linkTo(`/works/article/${item.slug}`)">
              <div class="img">
                <cdn-img :src="item.image" v-if="matchMediaType(item.image, 'image')"/>
                <cdn-video :src="item.image" loop autoplay muted playsinline v-if="matchMediaType(item.image, 'video')"/>
              </div>
            </div>
          </div>
        </div>
      </Slider>
    </section>
  </main>
</template>

<script>
import oax from '@/utils/oax';
import CdnImg from '@/views/components/CdnImg';
import Slider from '@/views/components/Slider';
import AudioPlayer from '@/views/components/AudioPlayer';
import Title from '@/views/components/modules/Title';
import BasicText from '@/views/components/modules/BasicText';
import TitleText from '@/views/components/modules/TitleText';
import Quote from '@/views/components/modules/Quote';
import Profile from '@/views/components/modules/Profile';
import Footnote from '@/views/components/modules/Footnote';
import DotList from '@/views/components/modules/DotList';
import NumberList from '@/views/components/modules/NumberList';
import Buttons from '@/views/components/modules/Buttons';
import SingleImage from '@/views/components/modules/SingleImage';
import Banner from '@/views/components/modules/Banner';
import DoubleImage from '@/views/components/modules/DoubleImage';
import DoubleImageVideo from '@/views/components/modules/DoubleImageVideo';
import DoubleImageText from '@/views/components/modules/DoubleImageText';
import Carousel from '@/views/components/modules/Carousel';
import CarouselDot from '@/views/components/modules/CarouselDot';
import LinkUpload from '@/views/components/modules/LinkUpload';
import SingleVideo from '@/views/components/modules/SingleVideo';
import Blank from '@/views/components/modules/Blank';
import Reference from '@/views/components/modules/Reference';
import Author from '@/views/components/modules/Author';
import Audio from '@/views/components/modules/Audio';
import SnsShare from '@/views/components/SnsShare';
import { i18n } from '@/plugins/i18n';
import CdnVideo from '@/views/components/CdnVideo';
import Award from '@/views/components/modules/Award.vue';
import Credit from '@/views/components/modules/Credit.vue';
import CarouselLine from '@/views/components/modules/CarouselLine.vue';
import { getLocalObject } from '@/utils/LocalObject';
import DoubleVideo from '@/views/components/modules/DoubleVideo.vue';
import TripleMedia from '@/views/components/modules/TripleMedia.vue';
import Media from '@/views/components/modules/Media.vue';

export default {
  name: 'works-article',
  components: {
    Media,
    TripleMedia,
    DoubleVideo,
    DoubleImageVideo,
    DoubleImageText,
    CarouselLine,
    Credit,
    Award,
    CdnVideo,
    SnsShare,
    Audio,
    Author,
    Reference,
    Blank,
    SingleVideo,
    LinkUpload,
    CarouselDot,
    Carousel,
    DoubleImage,
    Banner,
    SingleImage,
    Buttons,
    NumberList,
    DotList,
    Footnote,
    Profile,
    Quote,
    TitleText,
    BasicText,
    Title,
    AudioPlayer,
    Slider,
    CdnImg,
  },
  data() {
    return {
      info: null,
      related: null,
    };
  },
  metaInfo() {
    if (!this.info) return null;
    return {
      title: this.info.title,
      meta: [
        { vmid: 'title', content: this.info.metaTitle || this.$t('meta.title') },
        { vmid: 'description', content: this.info.metaDesc || this.$t('meta.description') },
        {
          vmid: 'image',
          content: this.info.metaImage ? `${ process.env.VUE_APP_CDN_HOST }/${ this.mediaVal(this.info.metaImage).location }` : '/img/og.png',
        },
        { vmid: 'path', content: this.$route.path.replace(new RegExp(`^/(${i18n.routeMatches()})`), '') },
      ],
    };
  },
  watch: {
    info: {
      handler(v) {
        v && this.$store.commit('theme', `${ v.theme }`);
        v && this.$store.commit('bgColor', `${ v.bgColor }`);
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    theme() {
      return this.info?.theme;
    },
    bgColor() {
      return this.info?.bgColor;
    },
    type() {
      return this.info?.type;
    },
    title() {
      return this.info?.title;
    },
    content() {
      return this.info?.content;
    },
    tags() {
      return this.info?.tagList?.split(',');
    },
    heroTags() {
      return this.tags?.slice(0, 2);
    },
    categories() {
      return this.info?.categoryList?.split(',');
    },
    lgTitle() {
      return ['about', 'contact', 'career'].some(item => item === this.$route.params.slug);
    },
  },
  methods: {
    onSearch(tag) {
      // this.$root.$emit('search', tag);
      this.$store.commit('isFirst', true);
      this.$router.replace({ query: { searchData: tag } });
    },
  },
  async mounted() {
    if (this.$route.meta.preview && typeof window !== 'undefined') {
      const localObject = getLocalObject('preview');
      this.info = localObject.get(this.$route.params.slug);
      if (!this.info) return;
      const contents = JSON.parse(this.info.contents);
      this.info.hero = contents.hero;
      this.info.content = contents.content;
      this.related = (await oax.get(`/api/article/related/${ this.info.sq }`, { lang: this.$route.params.locale || 'ko' })).data;
    }
    this.$store.commit('theme', `${ this.theme }`);
    this.$store.commit('bgColor', `${ this.bgColor }`);
    console.log(this.theme);
  },
  async asyncData({ route }) {
    if (route.meta.preview) return;
    const info = (await oax.get(`/api/article/${ encodeURIComponent(route.params.slug) }`, { lang: route.params.locale || 'ko' })).data;
    if (!info) throw new Error('404');
    const related = (await oax.get(`/api/article/related/${ info.sq }`, { lang: route.params.locale || 'ko' })).data;

    const contents = JSON.parse(info.contents);
    info.hero = contents.hero;
    info.content = contents.content;
    console.log(contents);

    return { info, related };
  },
};
</script>

<style lang="less">
@import '~@/less/proj.less';
[works-article] {
  a {text-decoration: underline; text-underline-position: under;}
  em {font-style: italic;}

  ul.tags {
    li { .ib; .mr(8); .mb(8);
      a { .block; .fs(13, 20); .p(3, 8); .br(3); .-a(#000); .c(#000); text-decoration: none;
        .no-touch &:hover { .bgc(#000); .c(#E3E3E3); }
        .dark & { .-a(#fff); .c(#E3E3E3); }
        .dark.no-touch &:hover { .bgc(#E3E3E3); .c(#000); }
      }
    }
  }

  section {
    &.hero { z-index: 2; .mb(20);
      .inner-wrap { .space-m; .pt(100); .pb(10); .rel; }
      h2 { .fs(24, 1.6); .bold;
        &.lgTitle { .fs(48, 1.2); .regular; }
      }
      .tags { .mt(20);
        li { .mb(0); }
      }
    }

    &.content {
      .desc { .fs(12, 20, -0.25); color: #888; .mt(10); .tc; .space-m;
        .dark & { color: #aaa; }
      }


      /* text */
      h5 { .fs(20, 1.6, -0.5); .bold; }
      p, .body-text { .fs(16, 1.8, -0.25);
        p { .min-h(16); }
      }
      > .body-text { .space-m; .mv(80); }
      .subtitle-text, .subtitle-text-en { .space-m; .mv(80);
        .body-text { .mt(20); }
      }
      .subtitle-text-vertical { .space-m; .mv(100);
        h5, .body-text p { .fs(32, 1.3, -0.25); }
        .body-text p { .o(0.6); .mt(30); }
      }
      .small-text { .space-m; .mv(80); .fs(14, 24, -0.25); color: #aaa; }

      .body-text ~ .basic-btn,
      .subtitle-text ~ .basic-btn,
      .subtitle-text-en ~ .basic-btn,
      .small-text ~ .basic-btn { .mt(-40); .mb(80); }

      /* button */
      .basic-btn { .space-m; .mv(20);
        a { .cta-btn;
          i {
            &.landing { -webkit-mask-image: url('/img/common/ic-arrow-right-cta.svg'); mask-image: url('/img/common/ic-arrow-right-cta.svg'); }
            &.download { -webkit-mask-image: url('/img/common/ic-arrow-down-cta.svg'); mask-image: url('/img/common/ic-arrow-down-cta.svg'); }
          }
        }
      }

      /* image */
      .media { .space-m; .mv(20);
        img, video { .wf; .block; }
        li + li { .mt(20); }
      }
      .basic-image, .basic-image-zero, .small-image, .small-image-zero { .space-m; }
      .basic-image, .basic-image-zero, .full-image, .full-image-zero, .small-image, .small-image-zero {
        img { .wf; .block; }
      }
      .basic-image, .full-image, .small-image { .mv(20); }
      .double-image, .double-image-video, .double-video-image, .double-image-text, .double-text-image, .double-video, .triple { .space-m; .mv(20);
        .left { .mb(20); }
        img, video { .block; .wf; }
      }
      .double-image-text, .double-text-image {
        .subtitle-text { .p; .m; width: auto; .block; }
      }
      .triple {
        li + li { .mt(20); }
      }
      .line-carousel { .space-m; .mv(20); .rel;
        .swiper-container {
          .slide-item { .wf;
            .image-area { .wf; overflow: hidden;
              img { .block; object-fit: cover; .wf; }
            }
            &:last-of-type { .m(0); }
          }
        }
        .swiper-navigation { .abs; .lt(0, 50%); .wf;
          .swiper-button-prev, .swiper-button-next { color: transparent; .wh(24); .contain; .block; .bg('/img/common/carousel-arrow.svg'); }
          .swiper-button-prev { .lt(30, 50%); transform: rotate(180deg); }
          .swiper-button-next { .rt(30, 50%); }
          .swiper-button-disabled {.o(0.2);}
        }
        .swiper-scrollbar { .wf; .h(2); .bgc(rgba(0, 0, 0, .2)); .mt(10);
          .swiper-scrollbar-drag { .bgc(#000); }
          .dark & { .bgc(rgba(255, 255, 255, .2));
            .swiper-scrollbar-drag { .bgc(#fff); }
          }
        }
      }
      /* video */
      .single-video, .single-video-loop, .youtube, .vimeo { .space-m; .mv(20);
        video { .block; .wf; }
        .video-wrap { aspect-ratio: 16/9; }
        iframe { .block; .f; }
      }
      .big-video, .big-video-loop { .wf;
        video { .block; .wf; }
      }
      .award { .space-m;
        li { .mv(20); .pl(76); .bg-yc; .no-repeat; .bg-h(42); .min-h(42); .flex; .align-center;
          p { .fs(14, 24); }
          &.red { .bg('/img/common/award-red.png'); }
          &.if { .bg('/img/common/award-if.png'); }
          &.idea { .bg('/img/common/award-idea.png'); }
          &.german { .bg('/img/common/award-german.m.png'); }
          .dark &.idea { .bg('/img/common/award-idea-w.png'); }
          .dark &.german { .bg('/img/common/award-german-w.m.png'); }
        }
      }
      .credit { .space-m; .mv(80);
        h5 { .mb(20); }
      }
      // hr 기본 스타일(overflow: hidden)과 base.css의 height: 0 때문에 :before 라인이 잘려 안 보이던 문제 수정
      // 색상은 푸터 구분선과 동일 (흰 배경 #ebebeb / 검정 배경 #3c3c3c)
      .line { .space-m; .mv(20); height: auto; overflow: visible;
        &:before { .cnt; .wh(100%, 1); .bgc(#ebebeb); }
        .dark &:before { .bgc(#3c3c3c); }
      }
      .blank {
        &.blank-20 {.h(20);}
        &.blank-40 {.h(30);}
        &.blank-60 {.h(40);}
        &.blank-100 {.h(60);}
        &.blank-120 {.h(80);}
      }
    }

    &.tag { .space-m; .pv(15); }
    &.related { .mt(55); .rel; .-box; .crop;
      h3 { .fs(18, 1.5); .semi-bold; .mb(30); .space-m; }
      .swiper-container { overflow: visible; .space-m;
        .swiper-wrapper {
          .swiper-slide { .mr(20); .w(90%);
            &:last-child { .m; }
            .slider-item { .rel; .f; .-box; cursor: pointer;
              .inner { .rel; .f; overflow: hidden;
                .img { aspect-ratio: 16/9; .crop; .br(10); }
                [cdn-img], [cdn-video] { .rel; .f; object-fit: cover; transform: scale(1); transition: transform 1s cubic-bezier(0, 0, 0.13, 1.04), opacity .6s ease; }
                p { .fs(24, 30); .mt(16); }
                p + p { .fs(16, 24, -0.25); .mt(8); }
              }
              .no-touch &:hover {
                [cdn-img], video {transform: scale(1.1);}
                &.has-video [cdn-img] { .o(0); }
              }
            }
          }
        }
      }
      .swiper-navigation { .hide; }
    }

    &.rounded {
      [module-item] {
        img, video, .video-wrap { .br(10); .crop; }
      }
    }
  }
}

@media (min-width: @screen-tp-min) {
  [works-article] {
    section {
      &.content {
        .basic-carousel { .p(60, 0); .rel;
          .swiper-container { .w(375); .m(0);
            .slide-item { .mr(20);
              .image-area {.h(236); .wf;}
              .desc {.pt(20);}
            }
          }
          .swiper-navigation {.block;}
        }
        .line { .mt(20); .mb(20);}
      }
      &.related {
        .swiper-navigation { .block; .abs; .rt(20, -15);
          .swiper-button-prev, .swiper-button-next { .ib; .static; .m; color: transparent; .wh(52); .contain('/img/common/seemore-arrow.svg');
            .dark & { .bg('/img/common/seemore-arrow-w.svg'); }
          }
          .swiper-button-next { transform: rotate(180deg); }
          .swiper-button-disabled {.o(0.2);}
        }
      }
    }
  }
}

@media (min-width: @screen-tl-min) {
  [works-article] {
    section {
      &.hero {
        .inner-wrap { .pt(120); }
        .text-area { .flex; justify-content: space-between; align-items: flex-end; }
        h2 { .fs(30, 1.6);
          &.lgTitle { .fs(80, 1.2); }
        }
        .tags { .mt(0); }
      }
      &.content {
        .subtitle-text, .subtitle-text-en { .grid(2, 20);
          .body-text { .mt(0); }
        }
        .subtitle-text-vertical { .mv(200);
          h5, .body-text p { .fs(40, 1.3, -0.25); }
        }
        .basic-btn {
          a { .ml(calc(50% + 10px)); width: auto; }
        }
        .media {
          &.cols-2 ul { .grid(2, 20); }
          &.cols-3 ul { .grid(3, 20); }
          li + li { .mt(0); }
        }
        .double-image, .double-video, .double-image-video, .double-video-image, .double-image-text, .double-text-image { .grid(2, 20); grid-row-gap: 0;
          .left { .m; }
          .desc { grid-column: span 2; }
        }
        .double-video-image .left { order: 2 }
        .double-text-image .left { order: 2 }
        .triple {
          ul { .grid(3, 20); }
          li + li { .mt(0); }
        }
        .credit { .grid(2, 20); }
      }
      &.tag { .space-d; .pv(20); }
    }
  }
}

@media (min-width: @screen-dm-min) {
  [works-article] {
    section {
      &.hero {
        .inner-wrap { .space-d; }
        h2 { .fs(36, 1.6); }
      }
      &.content {
        p, .body-text { .fs(18, 1.8, -0.25);
          p { .min-h(18); }
        }
        > .body-text {
          p { .m; }
        }
        .subtitle-text, .subtitle-text-en { .space-d;
          h5 { .fs(24, 1.5, -0.5); }
        }
        .subtitle-text-vertical { .mv(220); .space-d;
          h5, .body-text p { .fs(48, 1.3, -0.25); }
        }
        .small-text { .space-d;
          p { .m; }
        }
        .basic-btn { .space-d; }
        .media { .space-d; }
        .small-image, .small-image-zero { .space-d; }
        .basic-image, .basic-image-zero { .space-d; }
        .basic-image, .basic-image-zero, .full-image, .full-image-zero {
          .desc { .mt(20); }
        }
        .double-image, .double-image-video, .double-video-image, .double-image-text, .double-text-image, .double-video { .space-d; .grid(2, 20); grid-row-gap: 0; }
        .line-carousel { .space-d;
          .swiper-scrollbar { .mt(20); }
          .swiper-navigation {
            .swiper-button-prev, .swiper-button-next { .wh(36); }
            .swiper-button-prev { .lt(60, 50%); }
            .swiper-button-next { .rt(60, 50%); }
          }
        }
        .single-video, .single-video-loop, .youtube, .vimeo, .triple { .space-d; }
        .award { .space-d; .pv(40);
          ul { .m; .grid(3, 40); }
          li { .p(84, 0, 0); background-position: 0 0; .bg-h(64); .block;
            p { .fs(16, 26); }
            &.german { .bg('/img/common/award-german.png'); }
            .dark &.german { .bg('/img/common/award-german-w.png'); }
          }
        }
        .credit { .space-d;
          h5 { .fs(24, 1.5); .semi-bold; .mb(0); }
        }
        .blank {
          &.blank-20 {.h(20);}
          &.blank-40 {.h(40);}
          &.blank-60 {.h(60);}
          &.blank-100 {.h(100);}
          &.blank-120 {.h(120);}
        }
        .line { .space-d; }
      }
      &.related { .space-d;
        h3 { .p; .fs(24, 1.5); }
        .swiper-container { overflow: visible; .p; }
      }
    }
  }
}
@media (min-width: @screen-dx-min) {
  [works-article] {
    section {
      &.hero {
        .inner-wrap { .pt(100); }
        h2 {
          &.lgTitle { .fs(120, 1.2); }
        }
      }
      &.content {
        .body-text { .fs(20, 36, -0.25); }
        .subtitle-text-vertical { .mv(250);
          h5, .body-text p { .fs(62, 1.3, -0.25); }
        }
        .award {
          ul { .grid(3, 80); }
          li { .p(100, 0, 0); .bg-h(80); }
        }
        .blank {
          &.blank-20 {.h(40);}
          &.blank-40 {.h(60);}
          &.blank-60 {.h(100);}
          &.blank-100 {.h(160);}
          &.blank-120 {.h(200);}
        }
      }
    }
  }
}
</style>
